import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { api, ProjectActionProgress } from '../../../services/api';
import { EmptyNote, formatDateTime, paHeadCellSx } from './shared';

const Stat: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Paper
    variant="outlined"
    sx={{
      px: 2,
      py: 1.25,
      flex: 1,
      minWidth: 120,
      bgcolor: 'transparent',
      borderColor: 'rgb(var(--color-text-rgb) / 0.12)',
    }}
  >
    <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'rgb(var(--color-text-rgb) / 0.6)' }}>
      {label}
    </Typography>
  </Paper>
);

/** Share of `total` that `value` represents, as a 0-100 bar value. */
function pct(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

/**
 * Management's read on the whole programme: what has been handed out, what has
 * actually been called, and how much each caller is doing.
 */
const ProgressTab: React.FC = () => {
  const [data, setData] = useState<ProjectActionProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getProjectActionProgress());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }
  if (!data || data.totals.total === 0) {
    return <EmptyNote>No agents have been put on a project yet.</EmptyNote>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Stat label="Agents on lists" value={data.totals.total} />
        <Stat label="Assigned to a caller" value={data.totals.assigned} />
        <Stat label="Marked called" value={data.totals.called} />
        <Stat label="WhatsApp links sent" value={data.totals.linksSent} />
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          By project
        </Typography>
        <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={paHeadCellSx}>Project</TableCell>
                <TableCell sx={paHeadCellSx} align="center">Agents</TableCell>
                <TableCell sx={{ ...paHeadCellSx, minWidth: 160 }}>Assigned</TableCell>
                <TableCell sx={{ ...paHeadCellSx, minWidth: 160 }}>Called</TableCell>
                <TableCell sx={paHeadCellSx} align="center">Links sent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.projects.map((row) => (
                <TableRow key={row.projectId} hover>
                  <TableCell>{row.projectName || row.projectId}</TableCell>
                  <TableCell align="center">{row.total}</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">{`${row.assigned} / ${row.total}`}</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={pct(row.assigned, row.total)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">{`${row.called} / ${row.total}`}</Typography>
                      <LinearProgress
                        variant="determinate"
                        color="success"
                        value={pct(row.called, row.total)}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{row.linksSent}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          By caller
        </Typography>
        {data.callers.length === 0 ? (
          <EmptyNote>Nothing has been assigned to a caller yet.</EmptyNote>
        ) : (
          <TableContainer sx={{ maxHeight: 320, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={paHeadCellSx}>Caller</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Assigned</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Called</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Links sent</TableCell>
                  <TableCell sx={paHeadCellSx} align="center">Outstanding</TableCell>
                  <TableCell sx={paHeadCellSx}>Last activity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.callers.map((caller) => {
                  const outstanding = caller.assigned - caller.called;
                  return (
                    <TableRow key={caller.ceaNumber} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {caller.name || caller.ceaNumber}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgb(var(--color-text-rgb) / 0.5)' }}
                        >
                          {caller.ceaNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{caller.assigned}</TableCell>
                      <TableCell align="center">{caller.called}</TableCell>
                      <TableCell align="center">{caller.linksSent}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={outstanding}
                          color={outstanding === 0 ? 'success' : 'warning'}
                          variant={outstanding === 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(caller.lastActivityAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
};

export default ProgressTab;
